import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ConfirmModal from "../../../../components/ConfirmModal";
import Input from "../../../../components/Form/Input/input";
import { Label as LabelText } from "../../../../components/Form/Label/styles";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box } from "../../../../components/Layout";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { useCurrentPage, withPage } from "../../../../hooks/CurrentPage";
import { HighlightsSection, HighlightItem } from "../../../../model/site";
import { clone, deepEqual } from "../../../../utils/objects";
import ItemsTable from "../../components/ItemsTable";
import { useSection } from "../../hooks/Section";
import { navigationState } from "../Sections/navigationState";
import { HighlightsNavigationState } from "./highlightsNavigationState";
import AlignCenter from "./pages/Item/components/icons/AlignCenter";
import AlignLeft from "./pages/Item/components/icons/AlignLeft";
import { Container } from "./styles";

const HighlightsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // sub-screens keep the page of the Keepo being edited (?page)
  const { pageId } = useCurrentPage();

  const highlightsNavigationState = useRef(navigationState.get() as HighlightsNavigationState);
  const restoreState = useCallback(
    (newly: unknown, section: HighlightsSection) => {
      if (highlightsNavigationState.current) {
        section.pageTitle = highlightsNavigationState.current.currentSection.pageTitle;
        section.items = highlightsNavigationState.current.currentSection.items;
        section.alignment = highlightsNavigationState.current.currentSection.alignment;
      } else if (newly) {
        section.pageTitle = t("pages.editor.highlights.header.title");
        section.items = [];
        section.alignment = "LEFT";
      }
      return section;
    },
    [t, highlightsNavigationState]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<HighlightsSection>(
    "highlights",
    restoreState
  );

  const [idRemoving, setIdRemoving] = useState<string>();

  const onAddClick = () => {
    goEditItemPage();
  };

  const onEditClick = (id: string) => {
    goEditItemPage(id);
  };

  const goEditItemPage = (id?: string) => {
    const state: HighlightsNavigationState = {
      currentSection: clone<HighlightsSection>(section),
      defaultSection: highlightsNavigationState.current?.defaultSection || clone<HighlightsSection>(section),
      editingHighlight: section.items.find(highlight => highlight.id === id),
    };
    navigationState.set(state);
    navigate(withPage("/editor/highlights/item", pageId));
  };

  const onRemoveClick = (highlightId: string) => {
    setIdRemoving(highlightId);
  };

  const removeHighlight = () => {
    const items = [...section.items];
    const index = items.findIndex(({ id }) => id === idRemoving);

    items.splice(index, 1);
    setSection({ ...section, items });
    setIdRemoving(undefined);
  };

  const hasUnsavedChangesWrapper = (): boolean => {
    return highlightsNavigationState?.current?.defaultSection
      ? !deepEqual(section, highlightsNavigationState.current.defaultSection)
      : hasUnsavedChanges();
  };

  const ButtonsAlignment = ({
    value = "LEFT",
    onClick,
  }: {
    value: "LEFT" | "CENTER" | "RIGHT";
    onClick: () => void;
  }) => {
    return (
      <button
        type="button"
        // named as Personalizar's alignment, which it follows
        aria-label={t(`pages.editor.settings.form.advancedCustomization.inputs.aligment.${value.toLowerCase()}`)}
        aria-pressed={section.alignment === value}
        onClick={onClick}
        style={{
          padding: "16px 14px",
          backgroundColor: section.alignment === value ? "#3A3F50" : "transparent",
          color: "#fff",
          border: section.alignment === value ? "2px solid #3A3F50" : "2px solid #262A34",
          borderRadius: "12px",
          cursor: "pointer",
          fontWeight: section.alignment === value ? "600" : "400",
        }}>
        {/* on the right: the left's lines, mirrored */}
        {value === "LEFT" ? (
          <AlignLeft />
        ) : value === "RIGHT" ? (
          <AlignLeft style={{ transform: "scaleX(-1)" }} />
        ) : (
          <AlignCenter />
        )}
      </button>
    );
  };

  return (
    <Container>
      <SubHeader
        title={t("pages.editor.highlights.header.title")}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChangesWrapper}
      />
      {idRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.highlights.actions.remove.confirmModal.title")}
          cancelTitle={t("pages.editor.highlights.actions.remove.confirmModal.cancelButton")}
          confirmButtonTitle={t("pages.editor.highlights.actions.remove.confirmModal.confirmButton")}
          onCancel={() => setIdRemoving(undefined)}
          onConfirm={removeHighlight}
        />
      )}
      <Form>
        <Box borderBottom>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              required
              value={section.pageTitle}
              onChange={e =>
                setSection({
                  ...section,
                  pageTitle: e.target.value,
                })
              }
              label={t("pages.editor.highlights.form.pageTitle")}
            />
          </FormGroup>
        </Box>
        <Box borderBottom>
          <FormGroup>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}>
              <LabelText as="p">{t("pages.editor.highlights.form.alignment")}</LabelText>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                }}>
                <ButtonsAlignment
                  value="LEFT"
                  onClick={() =>
                    setSection({
                      ...section,
                      alignment: "LEFT",
                    })
                  }
                />
                <ButtonsAlignment
                  value="CENTER"
                  onClick={() =>
                    setSection({
                      ...section,
                      alignment: "CENTER",
                    })
                  }
                />
                <ButtonsAlignment
                  value="RIGHT"
                  onClick={() =>
                    setSection({
                      ...section,
                      alignment: "RIGHT",
                    })
                  }
                />
              </div>
            </div>
          </FormGroup>
        </Box>
        <Box>
          <ItemsTable
            onSortUpdate={items =>
              setSection({
                ...section,
                items: items as HighlightItem[],
              })
            }
            onAddClick={onAddClick}
            onEditClick={onEditClick}
            onRemoveClick={onRemoveClick}
            items={section.items}
            title={t("pages.editor.highlights.form.table.title")}
            addButtonTitle={t("pages.editor.highlights.form.table.addButton")}
          />
        </Box>
        <SaveBar onSave={saveSection} hasUnsavedChanges={hasUnsavedChangesWrapper} />
      </Form>
    </Container>
  );
};

export default HighlightsPage;
